import type { NestMonsterType } from '@/types/monster'
import type { PartnerType, RoomType } from '@/types/room'
import { socketEmitAsync, socketOn } from '@/utils/web-socket'
import { dayjs } from 'element-plus'
import { defineStore } from 'pinia'
import type { Ref } from 'vue'

export const useRoomStore = defineStore('room', {
  state: () => {
    return {
      id: 0,
      name: '',
      partnerInfo: undefined as PartnerType | undefined,
      isRelease: true,
      isAutoStart: false,
      closeTime: '',
      autoCloseInterval: null as NodeJS.Timeout | null,
      isJoin: false,
      type: undefined as RoomType | undefined,
    }
  },
  actions: {
    async createRoom(type: RoomType, data: any = {}) {
      if (this.id) return ElMessage.warning('您正在房间内')
      const res = await socketEmitAsync<{ id: number; name: string }>({ type: 'createRoom', data: { type, ...data } })
      this.type = type
      this.id = res.id
      this.name = res.name
    },
    async joinRoom(roomId: number) {
      const res = await socketEmitAsync<PartnerType & { monster: NestMonsterType }>({ type: 'joinRoom', data: { roomId } })
      this.partnerInfo = { id: res.id, nickname: res.nickname, nicknameColor: res.nicknameColor }
      this.type = res.monster.type
      this.id = roomId
      this.isJoin = true
      return res.monster
    },
    joinRoomById(monster: Ref<NestMonsterType | undefined>) {
      ElMessageBox.prompt('请输入房间号', '房号加入', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /^\d{6}$/,
        inputErrorMessage: '房间号为6位数字',
        confirmButtonClass: 'is-plain',
        beforeClose: async (action, instance, done) => {
          if (action !== 'confirm') return done()
          try {
            instance.confirmButtonLoading = true
            instance.confirmButtonText = '加载中...'
            const value = instance.inputValue
            const res = await this.joinRoom(Number(value))
            monster.value = res
            done()
          } finally {
            instance.confirmButtonLoading = false
            instance.confirmButtonText = '确定'
          }
        },
      })
    },
    async closeRoom() {
      ElMessageBox.confirm('确定关闭房间吗？', '提示', {
        type: 'warning',
        dangerouslyUseHTMLString: true,
        appendTo: 'body',
        confirmButtonClass: 'is-plain',
        beforeClose: async (action, instance, done) => {
          if (action !== 'confirm') return done()
          try {
            instance.confirmButtonLoading = true
            instance.confirmButtonText = '加载中...'
            await socketEmitAsync({ type: 'closeRoom' })
            this.clearRoomInfo()
            ElMessage.success('关闭成功')
            done()
          } finally {
            instance.confirmButtonLoading = false
            instance.confirmButtonText = '确定'
          }
        },
      })
    },
    async releaseRoom() {
      await socketEmitAsync({ type: 'releaseRoom', data: { isRelease: !this.isRelease } })
      if (this.isRelease) ElMessage.success('已取消发布')
      else ElMessage.success('已发布')
      return true
    },
    toggleAutoStart() {
      this.isAutoStart = !this.isAutoStart
      if (this.isAutoStart) ElMessage.success('已开启自动开始')
      else ElMessage.success('已关闭自动开始')
      return true
    },
    async quitRoom() {
      await socketEmitAsync({ type: 'quitRoom' })
      this.clearRoomInfo()
      ElMessage.success('退出成功')
    },
    clearRoomInfo() {
      this.id = 0
      this.name = ''
      this.partnerInfo = undefined
      this.isRelease = true
      this.isAutoStart = false
      this.closeTime = ''
      this.type = undefined
    },
    autoCloseRoom() {
      this.closeTime = dayjs().add(5, 'minute').format('YYYY-MM-DD HH:mm:ss')
      if (this.autoCloseInterval) clearInterval(this.autoCloseInterval)
      this.autoCloseInterval = setInterval(async () => {
        if (dayjs().isAfter(dayjs(this.closeTime))) {
          await socketEmitAsync({ type: 'closeRoom' })
          this.clearRoomInfo()
          ElMessage.success('已自动关闭房间')
        }
      }, 1000)
    },
    socketOnInit() {
      socketOn({
        type: 'nestPlayerJoin',
        callBack: (res: PartnerType) => {
          this.partnerInfo = res
        },
      })
      socketOn({
        type: 'roomPartnerQuit',
        callBack: () => {
          this.partnerInfo = undefined
        },
      })
      socketOn({
        type: 'roomClose',
        callBack: () => {
          this.clearRoomInfo()
          ElMessageBox.alert('房主关闭了房间', '提示')
        },
      })
    },
  },
})
